import React, { ReactNode, useEffect, useRef, useState } from "react";
import { CiMenuBurger } from "react-icons/ci";
import { FaHome, FaBullhorn, FaShoppingCart } from "react-icons/fa";
import { Link as LinkTo, useNavigate } from "react-router-dom";
import { getData } from "@/lib/utils";
import {
  getMyCompany,
  removeFromCart,
  updateAuthUser,
  updateCartItemQuantity,
} from "@/store/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { isUndefined } from "lodash";
import { MdSpaceDashboard } from "react-icons/md";
import { TbGiftFilled } from "react-icons/tb";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG, ROLE } from "@/types";
import { routes } from "@/consts";
import {
  Badge,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  Typography,
  AppBar,
  Toolbar,
  Box,
  Button as MuiButton,
  useTheme,
  alpha,
  Container,
  Fade,
} from "@mui/material";
import { AddCircle, Delete, RemoveCircle } from "@mui/icons-material";
import LanguageSelector from "@/components/language-selector";
import SidebarView from "./sidebar-view";
import { BiInfoCircle } from "react-icons/bi";
import { BadgeHelp, ShoppingBag } from "lucide-react";
import { ToastContainer } from "react-toastify";

interface NavPageProps {
  isForm: number;
  children: ReactNode;
}

const NavPage: React.FC<NavPageProps> = ({ isForm, children }) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [y, setY] = useState<number>(window.scrollY);
  const [sidebarOpen, toggleSidebar] = useState<boolean>(false);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;

  const toggleSideBarMenu = () => {
    toggleSidebar((prev) => !prev);
  };
  const handleCheckout = () => {
    navigate(routes.checkout);
  };

  // Simple scroll function to replace react-scroll
  const scrollToElement = (elementId: string, offset: number = 0) => {
    const element = document.getElementById(elementId);
    if (element) {
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    const swipeDistance =
      locale === LANG.EN
        ? touchEndX.current - touchStartX.current
        : touchStartX.current - touchEndX.current;

    if (swipeDistance > 150 && touchEndX.current > 50) {
      toggleSideBarMenu();
    }
  };
  const cart = useSelector((state: RootState) => state.auth.cart);

  const [anchor2El, setAnchor2El] = useState<null | HTMLElement>(null);
  const cartOpen = Boolean(anchor2El);
  const handleCartClose = () => {
    setAnchor2El(null);
  };
  const toggleCart = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchor2El(event.currentTarget);
  };
  const accesToken = getData("tokens")?.accessToken;
  const navigate = useNavigate();
  useEffect(() => {
    const handleScroll = () => setY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [y]);
  const handleQuantityChange = (id: string, quantity: number) => {
    if (quantity >= 1) {
      dispatch(updateCartItemQuantity({ id, quantity }));
    }
  };

  const handleRemoveItem = (id: string) => {
    dispatch(removeFromCart(id));
  };

  const navMenu = [
    {
      title: t("general.home"),
      path: routes.home,
      icon: <MdSpaceDashboard />,
      header: t("navigation.Dashboard"),
    },
    {
      title: t("general.LatestOpportunities"),
      path: routes.tenders,
      icon: <TbGiftFilled />,
      header: t("navigation.OpportunitiesList"),
    },
    {
      title: t("about.title"),
      path: routes.aboutUs,
      icon: <BadgeHelp />,
      header: t("about.title"),
    },
  ];

  return (
    <>
      <SidebarView
        sidebarOpen={sidebarOpen}
        list={navMenu}
        toggleSidebar={toggleSideBarMenu}
      />
      {sidebarOpen && (
        <div
          className="fixed top-0 left-0 w-full h-screen z-10"
          onClick={() => toggleSidebar(false)}
        />
      )}

      <AppBar
        position="fixed"
        elevation={y > 100 ? 4 : 0}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: `1px solid ${theme.palette.divider}`,
          transition: "all 0.3s ease-in-out",
          height: y > 100 ? 64 : 80,
          zIndex: 1200,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            sx={{
              height: "100%",
              justifyContent: "space-between",
              px: { xs: 1, sm: 2 },
            }}
          >
            {/* Logo */}
            <Fade in={true} timeout={600}>
              <MuiButton
                component={LinkTo}
                to={routes.home}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  textDecoration: "none",
                  textTransform: "none",
                  p: 0,
                  minWidth: "auto",
                }}
              >
                <Typography
                  variant="h4"
                  className="!bg-zinc-900"
                  sx={{
                    fontWeight: 800,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    ml: 2,
                  }}
                >
                  Medex
                </Typography>
              </MuiButton>
            </Fade>

            {/* Mobile Actions */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                gap: 1,
              }}
            >
              <LanguageSelector show={false} />
              {userRole === ROLE.BUYER && (
                <IconButton
                  onClick={toggleCart}
                  sx={{
                    color: "text.primary",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <Badge badgeContent={cart.length} color="error">
                    <FaShoppingCart size={20} />
                  </Badge>
                </IconButton>
              )}
              <IconButton
                onClick={() => toggleSidebar(!sidebarOpen)}
                sx={{
                  color: "text.primary",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <CiMenuBurger size={24} />
              </IconButton>
            </Box>

            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 2,
              }}
            >
              <Fade in={true} timeout={800}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {isForm === 1 ? (
                    <>
                      <MuiButton
                        onClick={() => scrollToElement("home", 80)}
                        startIcon={<FaHome size={16} />}
                        sx={{
                          color: "text.primary",
                          textTransform: "none",
                          fontWeight: 600,
                          gap: 1,
                          "& .MuiButton-startIcon": {
                            marginRight: locale === LANG.AR ? 0 : undefined,
                            marginLeft: locale === LANG.AR ? 0.5 : undefined,
                          },
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        {t("general.home")}
                      </MuiButton>
                      <MuiButton
                        component={LinkTo}
                        to={routes.marketPlace}
                        startIcon={<ShoppingBag size={16} />}
                        sx={{
                          color: "text.primary",
                          textTransform: "none",
                          fontWeight: 600,
                          gap: 1,
                          "& .MuiButton-startIcon": {
                            marginRight: locale === LANG.AR ? 0 : undefined,
                            marginLeft: locale === LANG.AR ? 0.5 : undefined,
                          },
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        {t("marketplace.title")}
                      </MuiButton>
                    </>
                  ) : (
                    <MuiButton
                      component={LinkTo}
                      to={routes.home}
                      startIcon={<FaHome size={16} />}
                      sx={{
                        color: "text.primary",
                        textTransform: "none",
                        fontWeight: 600,
                        gap: 1,
                        "& .MuiButton-startIcon": {
                          marginRight: locale === LANG.AR ? 0 : undefined,
                          marginLeft: locale === LANG.AR ? 0.5 : undefined,
                        },
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      {t("general.home")}
                    </MuiButton>
                  )}

                  <MuiButton
                    onClick={() => scrollToElement("latestOpportunities", 60)}
                    startIcon={<FaBullhorn size={16} />}
                    sx={{
                      color: "text.primary",
                      textTransform: "none",
                      fontWeight: 600,
                      gap: 1,
                      "& .MuiButton-startIcon": {
                        marginRight: locale === LANG.AR ? 0 : undefined,
                        marginLeft: locale === LANG.AR ? 0.5 : undefined,
                      },
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    {t("general.LatestOpportunities")}
                  </MuiButton>

                  <MuiButton
                    component={LinkTo}
                    to={routes.aboutUs}
                    startIcon={<BiInfoCircle size={16} />}
                    sx={{
                      color: "text.primary",
                      textTransform: "none",
                      fontWeight: 600,
                      gap: 1,
                      "& .MuiButton-startIcon": {
                        marginRight: locale === LANG.AR ? 0 : undefined,
                        marginLeft: locale === LANG.AR ? 0.5 : undefined,
                      },
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    {t("about.title")}
                  </MuiButton>
                </Box>
              </Fade>
            </Box>

            {/* Desktop Actions */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 2,
              }}
            >
              <Fade in={true} timeout={1000}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {!accesToken ? (
                    <MuiButton
                      component={LinkTo}
                      to={routes.login}
                      state={{ role: ROLE.BUYER }}
                      sx={{
                        color: "text.primary",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      {t("authentication.login")}
                    </MuiButton>
                  ) : (
                    <MuiButton
                      onClick={() => {
                        dispatch(updateAuthUser(null));
                        dispatch(getMyCompany(null));
                        localStorage.removeItem("tokens");
                        localStorage.removeItem("userInfo");
                        navigate(routes.home);
                      }}
                      sx={{
                        color: "text.primary",
                        textTransform: "none",
                        fontWeight: 600,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.error.main, 0.1),
                          borderColor: theme.palette.error.main,
                        },
                      }}
                    >
                      {t("navigation.Logout")}
                    </MuiButton>
                  )}

                  {!accesToken && (
                    <MuiButton
                      component={LinkTo}
                      to={routes.register}
                      variant="contained"
                      className="!bg-zinc-900"
                      sx={{
                        color: "white",
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        "&:hover": {
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      {t("actions.startNow")}
                    </MuiButton>
                  )}

                  <LanguageSelector />
                </Box>
              </Fade>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{ pt: y > 100 ? 8 : 10 }}
      >
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={locale === LANG.AR}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        {children}
      </Box>

      {/* Shopping Cart Menu */}
      <Menu
        anchorEl={anchor2El}
        open={cartOpen}
        onClose={handleCartClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            overflowY: "auto",
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            py: 2,
            fontWeight: 700,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {t("navigation.ShoppingCart")}
        </Typography>
        <List sx={{ py: 0 }}>
          {cart.length > 0 ? (
            cart.map((item) => (
              <ListItem
                key={item.productId}
                sx={{
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  py: 2,
                  px: 2,
                }}
              >
                {item.image ? (
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.unitName}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      objectFit: "cover",
                      mr: 2,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: alpha(theme.palette.grey[300], 0.5),
                      color: theme.palette.text.secondary,
                      mr: 2,
                    }}
                  >
                    {t("images.noImage")}
                  </Box>
                )}

                <Box sx={{ flexGrow: 1 }}>
                  <ListItemText
                    primary={item.unitName}
                    secondary={`${t(
                      "item.fields.price"
                    )}: ${item.unitPrice.toFixed(2)} ${t("currency")}`}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontWeight: 600,
                        fontSize: "0.875rem",
                      },
                      "& .MuiListItemText-secondary": {
                        fontSize: "0.75rem",
                        color: theme.palette.text.secondary,
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {t("checkout.fields.quantity")}:
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleQuantityChange(item.productId, item.quantity + 1)
                      }
                      sx={{
                        color: theme.palette.primary.main,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      <AddCircle fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="body2"
                      sx={{ minWidth: 20, textAlign: "center" }}
                    >
                      {item.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleQuantityChange(item.productId, item.quantity - 1)
                      }
                      sx={{
                        color: theme.palette.primary.main,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      <RemoveCircle fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <IconButton
                  size="small"
                  onClick={() => handleRemoveItem(item.productId)}
                  sx={{
                    color: theme.palette.error.main,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </ListItem>
            ))
          ) : (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {t("checkout.messages.emptyCart")}
              </Typography>
            </Box>
          )}
        </List>
        <Divider />
        <Box sx={{ p: 2, textAlign: "center" }}>
          <MuiButton
            variant="contained"
            onClick={handleCheckout}
            disabled={cart.length === 0}
            fullWidth
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              "&:hover": {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              },
              "&:disabled": {
                background: theme.palette.grey[300],
                color: theme.palette.grey[500],
              },
            }}
          >
            {t("navigation.Checkout")}
          </MuiButton>
        </Box>
      </Menu>
    </>
  );
};

export default NavPage;
